import { ReactComponent as PhoneIcon } from "@assets/icons/phone_default.svg"
import { Loader, Modal } from "@mantine/core"
import { useCurrentUserMutation, useGetUser } from "@utils/hooks"
import type React from "react"
import { useEffect, memo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useViewportSize } from "@mantine/hooks"
import BottomBarDesktop from "@components/BottomBars/BottomBarDesktop"

type ModalApplyPensionProps = {
  open: boolean
  onClose(): void
  pensionApplies: string
}

const ModalApplyPension: React.FC<ModalApplyPensionProps> = ({ open, onClose, pensionApplies }) => {
  const navigate = useNavigate()
  const { width: sizeWidth } = useViewportSize()
  const { mutate, isSuccess, isLoading } = useCurrentUserMutation()
  const { data: userData } = useGetUser()

  const onHandleBookingCall = useCallback(() => {
    navigate("/schedule-call", {
      state: {
        pensionApplies,
      },
    })
  }, [navigate, pensionApplies])

  const onApplyNow = useCallback(() => {
    if (userData?.employment_status === "Employee") {
      return mutate({
        process_pension_applied: pensionApplies,
        process_employer_details: "progress",
      })
    }

    if (
      userData?.employment_status === "Not currently working" ||
      userData?.employment_status === "Self-Employed/Sole Trader" ||
      userData?.employment_status === "Company Director"
    ) {
      mutate({
        process_pension_applied: pensionApplies,
        pension_applied: pensionApplies,
      })
    }
  }, [userData?.employment_status, mutate, pensionApplies])

  useEffect(() => {
    if (!isLoading && isSuccess) {
      if (userData?.employment_status === "Employee") {
        return navigate("share-employer")
      }

      return navigate("/pension-application")
    }
  }, [navigate, isSuccess, isLoading, userData])

  return (
    <Modal
      size={sizeWidth > 1024 ? 628 : 393}
      centered
      withCloseButton={false}
      opened={open}
      onClose={onClose}
      styles={{
        body: {
          padding: "0px",
        },
        content: {
          borderRadius: "16px",
        },
      }}
    >
      <div className="p-6 flex flex-col gap-y-6 lg:gap-y-8">
        <div className="flex flex-col gap-y-4 lg:gap-y-6">
          <div className="flex items-center justify-center rounded-full w-10 h-10 lg:w-[54px] lg:h-[54px] bg-pn_paper-3">
            <PhoneIcon className="w-6 h-6 lg:w-[33px] lg:h-[33px]" />
          </div>

          <div className="flex flex-col gap-y-2">
            <span className="text-pn-body-emphasis lg:text-pn-large-title lg:font-normal text-pn_gray-950">
              Talk to your advisor today
            </span>
            <span className="text-pn-callout-regular lg:text-pn-body-large-regular text-pn_gray-600">
              Book a call with your advisor and move closer to getting your Penny Pension
            </span>
          </div>

          <button onClick={onHandleBookingCall} className="btn-primary-lg block lg:hidden text-pn_gray-950 text-center">
            Book Call
          </button>
        </div>

        {userData?.employment_status !== "Employee" && (
          <div className="flex flex-col gap-y-6">
            <div className="flex flex-col gap-y-2">
              <span className="text-pn-body-emphasis text-pn_gray-950">Prefer to do it yourself?</span>
              <span className="text-pn-callout-regular text-pn_gray-600">
                For those who prefer a quicker approach, we made the application process as simple as possible by simple
                adding a few details about yourlself.
              </span>

              <div className="py-[6px] hidden lg:block">
                <button onClick={onApplyNow} disabled={isLoading} className="btn-tertiary-sm">
                  Start Application
                </button>
              </div>
            </div>

            <button
              onClick={onApplyNow}
              disabled={isLoading}
              className="flex btn-secondary-lg text-center lg:hidden items-center justify-center"
            >
              {isLoading ? <Loader size="sm" color="gray" /> : "Start Application"}
            </button>
          </div>
        )}

        <div className="hidden lg:block">
          <BottomBarDesktop
            message="Write supporting message"
            labelNext="Book Call"
            labelBack="Back"
            onNext={onHandleBookingCall}
            onBack={onClose}
            insideModal
          />
        </div>
      </div>
    </Modal>
  )
}
export default memo(ModalApplyPension)
